import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { workflowsApi } from '../services/api';
import { Workflow, CheckCircle, XCircle, Clock, ChevronRight, Timer } from 'lucide-react';

interface WorkflowStats {
  windowDays: number;
  runs: number;
  succeeded: number;
  failed: number;
  running: number;
  successRate: number | null;
  avgDurationMs: number | null;
  lastRunAt: string | null;
  timeSavedMinutes: number;
}

function formatTimeSaved(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

interface WorkflowSummary {
  id: string;
  name: string;
  description: string | null;
  updatedAt: string;
  stats: WorkflowStats;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    workflowsApi
      .getAll()
      .then((res) => setWorkflows(res.data.data.workflows || []))
      .catch(() => setError('Failed to load workflows'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-1">Published Workflows</h2>
        <p className="text-gray-400">
          Run activity over the last 30 days. Select a workflow to see its executions.
        </p>
      </div>

      {workflows.length === 0 ? (
        <div className="card text-center py-12">
          <Workflow className="mx-auto text-gray-600 mb-4" size={40} />
          <p className="text-gray-400">No published workflows yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {workflows.map((wf) => (
            <Link
              key={wf.id}
              to={`/workflows/${wf.id}/executions`}
              className="card block hover:bg-gray-700/60 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0">
                  <div className="p-3 bg-brand-orange/20 rounded-lg shrink-0">
                    <Workflow className="text-brand-orange" size={22} />
                  </div>
                  <div className="ml-4 min-w-0">
                    <p className="text-white font-medium truncate">{wf.name}</p>
                    {wf.description && (
                      <p className="text-gray-400 text-sm truncate">{wf.description}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-1">
                      Last run: {formatDate(wf.stats.lastRunAt)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-6 shrink-0 ml-4">
                  <div className="text-center hidden sm:block">
                    <p className="text-white text-lg font-semibold">{wf.stats.runs}</p>
                    <p className="text-gray-500 text-xs">Runs</p>
                  </div>
                  <div className="text-center hidden sm:block">
                    <p className="text-green-400 text-lg font-semibold flex items-center justify-center">
                      <CheckCircle size={14} className="mr-1" />
                      {wf.stats.succeeded}
                    </p>
                    <p className="text-gray-500 text-xs">Success</p>
                  </div>
                  <div className="text-center hidden sm:block">
                    <p className="text-red-400 text-lg font-semibold flex items-center justify-center">
                      <XCircle size={14} className="mr-1" />
                      {wf.stats.failed}
                    </p>
                    <p className="text-gray-500 text-xs">Failed</p>
                  </div>
                  <div className="text-center hidden md:block">
                    <p className="text-white text-lg font-semibold flex items-center justify-center">
                      <Clock size={14} className="mr-1 text-gray-400" />
                      {formatDuration(wf.stats.avgDurationMs)}
                    </p>
                    <p className="text-gray-500 text-xs">Avg time</p>
                  </div>
                  <div className="text-center hidden md:block">
                    <p className="text-white text-lg font-semibold">
                      {wf.stats.successRate === null ? '—' : `${wf.stats.successRate}%`}
                    </p>
                    <p className="text-gray-500 text-xs">Success rate</p>
                  </div>
                  {wf.stats.timeSavedMinutes > 0 && (
                    <div className="text-center hidden md:block">
                      <p className="text-teal-400 text-lg font-semibold flex items-center justify-center">
                        <Timer size={14} className="mr-1" />
                        {formatTimeSaved(wf.stats.timeSavedMinutes)}
                      </p>
                      <p className="text-gray-500 text-xs">Time saved</p>
                    </div>
                  )}
                  <ChevronRight className="text-gray-500" size={20} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
